import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Tickets from "./pages/Tickets";
import TicketDetail from "./pages/TicketDetail";
import Devices from "./pages/Devices";
import DeviceInventory from "./pages/EdrInventory";
import EdrConsole from "./pages/EdrConsole";
import EdrAgentDetail from "./pages/EdrAgentDetail";
import Device360 from "./pages/Device360";
import Servers from "./pages/Servers";
import Server360 from "./pages/Server360";
import Analytics from "./pages/Analytics";
import Clients from "./pages/Clients";
import Agents from "./pages/Agents";
import Client360 from "./pages/Client360";
import Contact360 from "./pages/Contact360";
import ProjectDetails from "./pages/ProjectDetails";
import AdminSettings from "./pages/AdminSettings";
import PortalApp from "./portal/PortalApp";
import ResetPassword from "./pages/ResetPassword"; // ← NEW
import StatusPage from "./pages/StatusPage"; // public system status
import DevTickets     from "./pages/DevTickets";
import DevTicketDetail from "./pages/DevTicketDetail";
import SupportChats    from "./pages/SupportChats";
import Infrastructure from "./pages/Infrastructure";
import KnowledgeBase from "./pages/KnowledgeBase";
import AdminReview from "./pages/AdminReview";
import ClientDatabase from "./pages/ClientDatabase";
import RegelisManagement from "./pages/RegelisManagement";
import LeadGeneration from "./pages/LeadGeneration";
import EEditions from "./pages/EEditions";
import Software from "./pages/Software";
import ApiKeys from "./pages/ApiKeys";
import SecuredData from "./pages/SecuredData";
import SendSecureData from "./pages/SendSecureData";
import ClientEmailSecurity from "./pages/ClientEmailSecurity";
import MyDay from "./pages/MyDay";
import TimeBilling from "./pages/TimeBilling";
import WpManagement from "./pages/WpManagement";
import { ROLE_ACCESS } from "./config/roles";
import { ThemeStyles } from "./theme";
import { ThemeProvider } from "./context/ThemeContext";
import MobileAppPrompt from "./components/MobileAppPrompt";
export { ROLE_ACCESS };

// Error boundary — shows the actual error instead of a blank screen
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null, info: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { this.setState({ info }); console.error("React error:", error, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24, color: "#f87171", background: "var(--t-page, #111b2f)", minHeight: "100vh", fontFamily: "monospace", fontSize: 13 }}>
          <h2 style={{ color: "#fca5a5", marginBottom: 12 }}>Page crashed</h2>
          <pre style={{ background: "#1f2937", padding: 12, borderRadius: 6, whiteSpace: "pre-wrap", color: "#fbbf24" }}>{String(this.state.error?.stack || this.state.error)}</pre>
          <pre style={{ background: "#1f2937", padding: 12, borderRadius: 6, whiteSpace: "pre-wrap", color: "#94a3b8", marginTop: 8 }}>{this.state.info?.componentStack}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

// =============================================================
//  ROUTE GUARD — Internal agents only
// =============================================================
const PrivateRoute = ({ children, page, requireRole }) => {
  const { agent, loading } = useAuth();

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", background:"var(--t-page, #111b2f)" }}>
      <div style={{ width:32, height:32, border:"3px solid var(--t-accent, #4f6fff)", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />
    </div>
  );

  if (!agent) return <Navigate to="/login" replace />;
  if (agent.role === "contact") return <Navigate to="/login" replace />;

  // Strict role gate (e.g. admin-only pages)
  if (requireRole) {
    const roles = agent.roles?.length ? agent.roles : [agent.role];
    const need  = Array.isArray(requireRole) ? requireRole : [requireRole];
    if (!need.some(r => roles.includes(r))) return <Navigate to="/dashboard" replace />;
  }

  // If a specific page is required, check role access
  if (page) {
    const allowed = ROLE_ACCESS[agent.role] || [];
    if (!allowed.includes(page)) return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// =============================================================
//  APP
// =============================================================
function App() {
  return (
    <BrowserRouter>
      <ThemeStyles />
      <Routes>

        {/* ── Public ─────────────────────────────────────────── */}
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} /> {/* ← NEW */}
        <Route path="/status" element={<StatusPage />} /> {/* public system status */}

        {/* ── White-labeled Customer Portal ──────────────────── */}
        {/* URL: /clientportal/acme-corp  */}
        <Route path="/clientportal/:identifier/*" element={<PortalApp />} />

        {/* ── Internal Agent App ──────────────────────────────── */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />

          {/* All roles */}
          <Route path="dashboard" element={
            <PrivateRoute page="dashboard"><Dashboard /></PrivateRoute>
          } />

          {/* All roles */}
          <Route path="tickets" element={
            <PrivateRoute page="tickets"><Tickets /></PrivateRoute>
          } />
          <Route path="tickets/:id" element={
            <PrivateRoute page="tickets"><TicketDetail /></PrivateRoute>
          } />

          {/* All roles */}
          <Route path="clients" element={
            <PrivateRoute page="clients"><Clients /></PrivateRoute>
          } />
          <Route path="clients/:id" element={
            <PrivateRoute page="clients"><Client360 /></PrivateRoute>
          } />
          <Route path="contacts/:id" element={
            <PrivateRoute page="clients"><Contact360 /></PrivateRoute>
          } />
          <Route path="dev-tickets" element={
            <PrivateRoute page="dev-tickets"><DevTickets /></PrivateRoute>
          } />
          <Route path="dev-tickets/:id" element={
            <PrivateRoute page="dev-tickets"><DevTicketDetail /></PrivateRoute>
          } />

          {/* Staff-wide — MIDTC Chats live-chat console */}
          <Route path="support-chats" element={
            <PrivateRoute page="support-chats"><ErrorBoundary><SupportChats /></ErrorBoundary></PrivateRoute>
          } />
          {/* "Projects & Maps" (renamed from Infrastructure 2026-08-07). The old
              /infrastructure URL keeps working for bookmarks/desktop shells;
              the page-permission key stays "infrastructure". */}
          <Route path="projects" element={
            <PrivateRoute page="infrastructure"><ErrorBoundary><Infrastructure /></ErrorBoundary></PrivateRoute>
          } />
          <Route path="infrastructure" element={
            <PrivateRoute page="infrastructure"><ErrorBoundary><Infrastructure /></ErrorBoundary></PrivateRoute>
          } />
          <Route path="projects/:id" element={
            <PrivateRoute requireRole="admin"><ErrorBoundary><ProjectDetails /></ErrorBoundary></PrivateRoute>
          } />

          {/* All roles */}
          <Route path="devices" element={
            <PrivateRoute page="devices"><Devices /></PrivateRoute>
          } />

          <Route path="inventory" element={
            <PrivateRoute page="devices"><DeviceInventory /></PrivateRoute>
          } />
          <Route path="edr-inventory" element={
            <PrivateRoute page="devices"><DeviceInventory /></PrivateRoute>
          } />
          <Route path="edr-console" element={
            <PrivateRoute page="devices"><EdrConsole /></PrivateRoute>
          } />
          <Route path="edr-agent/:agentId" element={
            <PrivateRoute page="devices"><EdrAgentDetail /></PrivateRoute>
          } />
          <Route path="devices/:deviceId" element={
            <PrivateRoute page="devices"><Device360 /></PrivateRoute>
          } />

          {/* Server RMM — staff-only (admin/manager/technician/dev) */}
          <Route path="server-rmm" element={
            <PrivateRoute page="server-rmm"><ErrorBoundary><Servers /></ErrorBoundary></PrivateRoute>
          } />
          <Route path="server-rmm/:serverId" element={
            <PrivateRoute page="server-rmm"><ErrorBoundary><Server360 /></ErrorBoundary></PrivateRoute>
          } />

          {/* Legacy iMacs (OS X 10.9-10.11) — staff-only; remote sessions are
              admin-gated inside the page, matching every other remote path. */}

          {/* All staff — internal Client & Website Registry */}
          <Route path="client-database" element={
            <PrivateRoute page="client-database"><ErrorBoundary><ClientDatabase /></ErrorBoundary></PrivateRoute>
          } />
          <Route path="client-database/clients/:id" element={
            <PrivateRoute page="client-database"><ErrorBoundary><ClientDatabase /></ErrorBoundary></PrivateRoute>
          } />
          {/* Lead Generation — owner-only; the API 404s everyone else (leadGen.js) */}
          <Route path="lead-generation" element={
            <PrivateRoute requireRole="admin"><ErrorBoundary><LeadGeneration /></ErrorBoundary></PrivateRoute>
          } />
          <Route path="regelis" element={
            <PrivateRoute requireRole="admin"><ErrorBoundary><RegelisManagement /></ErrorBoundary></PrivateRoute>
          } />

          {/* MIDTC Assistant — personal tasks, AI day plan, calendar */}
          <Route path="my-day" element={
            <PrivateRoute page="my-day"><ErrorBoundary><MyDay /></ErrorBoundary></PrivateRoute>
          } />
          {/* Time tracking & billing — admin/manager */}
          <Route path="time-billing" element={
            <PrivateRoute page="time-billing"><ErrorBoundary><TimeBilling /></ErrorBoundary></PrivateRoute>
          } />

          {/* MIDTC Software catalog (private GitHub-linked) */}
          <Route path="software" element={
            <PrivateRoute page="software"><ErrorBoundary><Software /></ErrorBoundary></PrivateRoute>
          } />
          {/* MIDTC API — personal assistant API keys */}
          <Route path="api-keys" element={
            <PrivateRoute page="api-keys"><ErrorBoundary><ApiKeys /></ErrorBoundary></PrivateRoute>
          } />
          {/* Client Email Security — O365 mail/mailbox posture + remediation */}
          <Route path="email-security" element={
            <PrivateRoute page="email-security"><ErrorBoundary><ClientEmailSecurity /></ErrorBoundary></PrivateRoute>
          } />
          {/* Secured Data — write-only credential vault (admin only) */}
          <Route path="secured-data" element={
            <PrivateRoute page="secured-data" requireRole="admin"><ErrorBoundary><SecuredData /></ErrorBoundary></PrivateRoute>
          } />
          {/* Client WordPress management (MIDTC Manage plugin) */}
          <Route path="send-secure-data" element={
            <PrivateRoute page="send-secure-data"><ErrorBoundary><SendSecureData /></ErrorBoundary></PrivateRoute>
          } />
          <Route path="wp-management" element={
            <PrivateRoute page="wp-management"><ErrorBoundary><WpManagement /></ErrorBoundary></PrivateRoute>
          } />
          {/* E-Editions — admin only (the /admin/eeditions API is admin-only too) */}
          <Route path="e-editions" element={
            <PrivateRoute page="e-editions" requireRole="admin"><ErrorBoundary><EEditions /></ErrorBoundary></PrivateRoute>
          } />

          {/* Admin / Manager only */}
          <Route path="analytics" element={
            <PrivateRoute page="analytics"><Analytics /></PrivateRoute>
          } />

          {/* All staff roles */}
          <Route path="knowledge-base" element={
            <PrivateRoute page="knowledge-base"><KnowledgeBase /></PrivateRoute>
          } />
          <Route path="knowledge-base/:id" element={
            <PrivateRoute page="knowledge-base"><KnowledgeBase /></PrivateRoute>
          } />

          {/* Admin / Manager only */}
          <Route path="agents" element={
            <PrivateRoute page="agents"><Agents /></PrivateRoute>
          } />

          {/* Admin only */}
          <Route path="admin-review" element={
            <PrivateRoute page="admin-review"><AdminReview /></PrivateRoute>
          } />

          {/* Admin / Manager only */}
          <Route path="settings" element={
            <PrivateRoute page="settings"><AdminSettings /></PrivateRoute>
          } />
        </Route>

        {/* ── Embed routes — no Layout/sidebar, used by desktop app iframe ── */}
        <Route path="/embed" element={<div style={{ height:"100vh", overflow:"hidden", background:"var(--t-page, #111b2f)", display:"flex", flexDirection:"column" }}><Outlet /></div>}>
          <Route path="infrastructure" element={
            <PrivateRoute page="infrastructure"><Infrastructure /></PrivateRoute>
          } />
          <Route path="dev-tickets" element={
            <PrivateRoute page="dev-tickets"><DevTickets /></PrivateRoute>
          } />
          <Route path="dev-tickets/:id" element={
            <PrivateRoute page="dev-tickets"><DevTicketDetail /></PrivateRoute>
          } />
          {/* Desktop app Tickets view (2026-10-23) — the same list + conversation as the web. */}
          <Route path="tickets" element={
            <PrivateRoute page="tickets"><div style={{ height:"100%", overflowY:"auto" }}><Tickets /></div></PrivateRoute>
          } />
          <Route path="tickets/:id" element={
            <PrivateRoute page="tickets"><TicketDetail /></PrivateRoute>
          } />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />

      </Routes>
    </BrowserRouter>
  );
}

export default function AppWithProviders() {
  return (
    <ThemeProvider>
      <App />
      <MobileAppPrompt />
    </ThemeProvider>
  );
}
